import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { projectService } from '../../services/project.service';
import { customerService } from '../../services/customer.service';
import { Project, ProjectFormData } from '../../types/project.types';
import { Customer } from '../../types';

const Glyph = ({ icon, size = '1.2rem' }: { icon: string; size?: string }) => (
  <Box
    component="span"
    className="material-symbols-outlined"
    sx={{
      fontSize: size,
      verticalAlign: 'middle',
      userSelect: 'none',
    }}
  >    {icon}
  </Box>
);

export const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { projectId } = useParams<{ projectId: string }>();
  const isEditMode = !!projectId;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({
    projectId: '',
    projectNo: '',
    projectName: '',
    projectType: '신규',
    customerId: '',
    customerName: '',
    status: '계획',
    description: '',
    startDate: new Date(),
    targetDate: undefined,
    tags: []
  });

  useEffect(() => {
    loadCustomers();
    if (isEditMode) {
      loadProject();
    }
  }, [projectId]);

  const loadCustomers = async () => {
    try {
      const data = await customerService.getAll();
      setCustomers(data);
    } catch (error) {
      console.error('고객사 로드 실패:', error);
    }
  };

  const loadProject = async () => {
    if (!projectId) return;
    
    try {
      const project = await projectService.getProjectById(projectId);
      if (project) {
        setFormData({
          projectId: project.projectId,
          projectNo: project.projectNo,
          projectType: project.projectType,
          projectName: project.projectName,
          customerId: project.customerId,
          customerName: project.customerName,
          status: project.status,
          description: project.description || '',
          startDate: project.startDate?.toDate() || null,
          targetDate: project.targetDate?.toDate() || null,
          tags: project.tags || []
        });
      }
    } catch (error) {
      console.error('프로젝트 로드 실패:', error);
      alert('프로젝트를 불러오는데 실패했습니다.');
    }
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData({
      ...formData,
      customerId,
      customerName: customer?.name || ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 필수값 검증
    if (!formData.projectNo || !formData.projectName || !formData.customerId) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditMode && projectId) {
        await projectService.updateProject(projectId, formData);
        alert('프로젝트가 수정되었습니다.');
      } else {
        // 프로젝트 번호 중복 체크
        const isDuplicate = await projectService.checkProjectNumber(formData.projectNo);
        if (isDuplicate) {
          alert('이미 사용중인 프로젝트 번호입니다.');
          return;
        }
        
        const newProjectId = await projectService.createProject(formData);
        alert('프로젝트가 등록되었습니다.');
        navigate(`/projects/${newProjectId}`);
        return;
      }
      
      navigate('/projects');
    } catch (error) {
      console.error('프로젝트 저장 실패:', error);
      alert('프로젝트 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, md: 3 }, py: 4 }}>
      {/* 헤더 */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={4}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Glyph icon={isEditMode ? 'edit_note' : 'add_circle'} size="1.8rem" />
          </Box>
          <Typography variant="h4" fontWeight={600}>
            {isEditMode ? '프로젝트 수정' : '프로젝트 등록'}
          </Typography>
        </Stack>
      </Stack>

      {/* 폼 카드 */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.light,
            0.05
          )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* 기본 정보 섹션 */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Glyph icon="info" size="1.2rem" />
                  기본 정보
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <TextField
                      fullWidth
                      label="프로젝트 번호"
                      value={formData.projectNo}
                      onChange={(e) => setFormData({ ...formData, projectNo: e.target.value })}
                      disabled={isEditMode}
                      required
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <TextField
                      fullWidth
                      label="프로젝트명"
                      value={formData.projectName}
                      onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                      disabled={isEditMode}
                      required
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* 고객사 및 상태 섹션 */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Glyph icon="business" size="1.2rem" />
                  고객사 및 상태
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <FormControl fullWidth required>
                      <InputLabel>고객사</InputLabel>
                      <Select
                        value={formData.customerId}
                        onChange={(e) => handleCustomerChange(e.target.value)}
                        label="고객사"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">고객사 선택</MenuItem>
                        {customers.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <FormControl fullWidth>
                      <InputLabel>프로젝트 타입</InputLabel>
                      <Select
                        value={formData.projectType}
                        onChange={(e) => setFormData({ ...formData, projectType: e.target.value as any })}
                        label="프로젝트 타입"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="신규">신규</MenuItem>
                        <MenuItem value="수정">수정</MenuItem>
                        <MenuItem value="개선">개선</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <FormControl fullWidth>
                      <InputLabel>상태</InputLabel>
                      <Select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        label="상태"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="계획">계획</MenuItem>
                        <MenuItem value="진행중">진행중</MenuItem>
                        <MenuItem value="완료">완료</MenuItem>
                        <MenuItem value="보류">보류</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {/* 설명 섹션 */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Glyph icon="description" size="1.2rem" />
                  프로젝트 설명
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="프로젝트에 대한 설명을 입력하세요..."
                  sx={{ borderRadius: 2 }}
                />
              </Box>

              {/* 일정 섹션 */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Glyph icon="schedule" size="1.2rem" />
                  일정
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="시작일"
                      value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value ? new Date(e.target.value) : null })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: '48%' } }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="목표일"
                      value={formData.targetDate ? new Date(formData.targetDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, targetDate: e.target.value ? new Date(e.target.value) : null })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* 버튼 */}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{ pt: 2 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/projects')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                  }}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                  }}
                >
                  {loading ? '저장중...' : (isEditMode ? '수정' : '등록')}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
